import React, { useState, useEffect } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import { 
  CalendarDaysIcon, 
  XMarkIcon, 
  CheckCircleIcon, 
  ClockIcon,
  VideoCameraIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { studentApi } from './studentApi';

// Note: Mock data removed - now using backend APIs

// Helper function to convert backend time windows format
// Note: Now fetching from backend, this is kept for reference/fallback
const convertBackendWindows = (windows) => {
  return windows.map(window => ({
    id: `${window.date}_${window.start_time}_${window.end_time}`,
    date: window.date,
    dateObj: new Date(window.date),
    startTime: window.start_time,
    endTime: window.end_time,
    type: 'window'
  }));
};

// Deprecated: Mock time windows generator (kept for fallback only)
const generateTimeWindows = () => {
  const windows = [];
  const today = new Date();
  const studentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Generate windows for next 14 days
  for (let day = 1; day <= 14; day++) {
    const date = new Date(today);
    date.setDate(today.getDate() + day);
    const dateStr = date.toISOString().split('T')[0];
    
    // Different availability patterns for different days
    if (day === 3) {
      // Specific example: Tutor available 2:30 PM - 8:00 PM on day 3
      windows.push({
        id: `window-${day}-1`,
        date: dateStr,
        dateObj: new Date(date),
        startTime: '14:30', // 2:30 PM
        endTime: '20:00',   // 8:00 PM
        type: 'window' // This is a continuous available window
      });
    } else if (day === 5) {
      // Another example: Morning availability
      windows.push({
        id: `window-${day}-1`,
        date: dateStr,
        dateObj: new Date(date),
        startTime: '09:00', // 9:00 AM
        endTime: '12:00',   // 12:00 PM
        type: 'window'
      });
      // Afternoon availability same day
      windows.push({
        id: `window-${day}-2`,
        date: dateStr,
        dateObj: new Date(date),
        startTime: '14:00', // 2:00 PM
        endTime: '18:00',   // 6:00 PM
        type: 'window'
      });
    } else if (day % 2 === 0) {
      // Even days: Standard availability
      windows.push({
        id: `window-${day}-1`,
        date: dateStr,
        dateObj: new Date(date),
        startTime: '10:00', // 10:00 AM
        endTime: '17:00',   // 5:00 PM
        type: 'window'
      });
    } else {
      // Odd days: Evening availability
      windows.push({
        id: `window-${day}-1`,
        date: dateStr,
        dateObj: new Date(date),
        startTime: '15:00', // 3:00 PM
        endTime: '21:00',   // 9:00 PM
        type: 'window'
      });
    }
  }
  
  return windows.sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return a.startTime.localeCompare(b.startTime);
  });
};

// Helper to generate quick slot options within a time window
const generateQuickSlots = (window) => {
  const slots = [];
  const [startHour, startMin] = window.startTime.split(':').map(Number);
  const [endHour, endMin] = window.endTime.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  const windowDuration = endMinutes - startMinutes;
  
  // Determine slot duration based on window size
  let slotDuration = 60; // Default to 1 hour
  let increment = 60; // Default increment (1 hour)
  
  if (windowDuration < 60) {
    // For windows less than 1 hour, use 30-minute slots
    slotDuration = 30;
    increment = 15; // Generate slots every 15 minutes for small windows
  } else if (windowDuration === 60) {
    // For exactly 1 hour window, generate one 1-hour slot
    slotDuration = 60;
    increment = 60; // Only one slot
  } else if (windowDuration < 120) {
    // For windows between 1-2 hours (but more than 1 hour), use 30-minute slots
    slotDuration = 30;
    increment = 30;
  }
  
  // Generate slots that fit within the window
  for (let minutes = startMinutes; minutes + slotDuration <= endMinutes; minutes += increment) {
    const slotStartHour = Math.floor(minutes / 60);
    const slotStartMin = minutes % 60;
    const slotEndMinutes = minutes + slotDuration;
    const slotEndHour = Math.floor(slotEndMinutes / 60);
    const slotEndMin = slotEndMinutes % 60;
    
    slots.push({
      startTime: `${slotStartHour.toString().padStart(2, '0')}:${slotStartMin.toString().padStart(2, '0')}`,
      endTime: `${slotEndHour.toString().padStart(2, '0')}:${slotEndMin.toString().padStart(2, '0')}`,
      duration: slotDuration
    });
  }
  
  // If no slots were generated (very small window), create one slot for the entire window
  if (slots.length === 0 && windowDuration > 0) {
    slots.push({
      startTime: window.startTime,
      endTime: window.endTime,
      duration: windowDuration
    });
  }
  
  return { quickSlots: slots, window };
};

// Helper to convert backend booking format to frontend format
const convertBackendBookings = (bookings) => {
  return bookings.map(booking => ({
    id: booking.id,
    tutorId: booking.tutor.id,
    tutorName: booking.tutor_name || booking.tutor.name,
    subject: booking.subject || booking.lesson_pack.subject,
    date: booking.date,
    startTime: booking.start_time,
    endTime: booking.end_time,
    status: booking.status.toLowerCase(),
    zoomLink: booking.meeting_link,
    meetingProvider: booking.meeting_provider,
    lessonPackId: booking.lesson_pack.id,
    lessonPackName: `${booking.lesson_pack.subject} - ${booking.lesson_pack.no_of_hours} hours`,
    student_display: booking.student_display || null,
    tutor_display: booking.tutor_display || null
  }));
};

// Deprecated: Mock bookings generator (kept for fallback only)
const generateMockBookings = () => {
  const bookings = [
    {
      id: 1,
      tutorId: 1,
      tutorName: 'Dr. Sarah Johnson',
      subject: 'Mathematics',
      date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      startTime: '14:00',
      endTime: '15:00',
      status: 'upcoming',
      zoomLink: 'https://zoom.us/j/123456789',
      lessonPackId: 1,
      lessonPackName: 'Algebra 1 - 10 hours'
    },
    {
      id: 2,
      tutorId: 2,
      tutorName: 'Prof. Michael Chen',
      subject: 'Physics',
      date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      startTime: '10:00',
      endTime: '11:00',
      status: 'upcoming',
      zoomLink: 'https://zoom.us/j/987654321',
      lessonPackId: 2,
      lessonPackName: 'Physics Fundamentals - 20 hours'
    },
    {
      id: 3,
      tutorId: 1,
      tutorName: 'Dr. Sarah Johnson',
      subject: 'Mathematics',
      date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      startTime: '16:00',
      endTime: '17:00',
      status: 'completed',
      zoomLink: 'https://zoom.us/j/111222333',
      lessonPackId: 1,
      lessonPackName: 'Algebra 1 - 10 hours'
    },
    {
      id: 4,
      tutorId: 1,
      tutorName: 'Dr. Sarah Johnson',
      subject: 'Mathematics',
      date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      startTime: '15:30', // Custom booking: 3:30 PM - 6:00 PM (example scenario)
      endTime: '18:00',
      status: 'upcoming',
      zoomLink: 'https://zoom.us/j/custombooking123',
      lessonPackId: 1,
      lessonPackName: 'Algebra 1 - 10 hours'
    }
  ];
  
  return bookings;
};

const Bookings = () => {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [tutors, setTutors] = useState([]);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [availableWindows, setAvailableWindows] = useState([]);
  const [selectedWindow, setSelectedWindow] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [customStartTime, setCustomStartTime] = useState('');
  const [customEndTime, setCustomEndTime] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [loadingTutors, setLoadingTutors] = useState(false);
  const [loadingWindows, setLoadingWindows] = useState(false);
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [error, setError] = useState(null);
  const [studentTimezone, setStudentTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Fetch bookings on component mount
  useEffect(() => {
    fetchBookings();
    fetchStudentTimezone();
  }, []);

  const fetchStudentTimezone = async () => {
    try {
      const profile = await studentApi.getStudentInfo();
      if (profile?.timezone) {
        setStudentTimezone(profile.timezone);
      }
    } catch (err) {
      console.error('Error fetching student timezone:', err);
      // Keep browser-detected timezone as fallback
    }
  };

  const fetchBookings = async () => {
    try {
      setLoadingBookings(true);
      setError(null);
      const data = await studentApi.getBookings();
      const converted = convertBackendBookings(data);
      setBookings(converted);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to load bookings. Please try again.');
      setBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  };

  // Fetch tutors when booking modal opens
  useEffect(() => {
    if (showBookingModal && tutors.length === 0) {
      fetchTutors();
    }
  }, [showBookingModal]);

  const fetchTutors = async () => {
    try {
      setLoadingTutors(true);
      setError(null);
      const data = await studentApi.getBookingTutors();
      
      // Convert backend format to frontend format
      const convertedTutors = [];
      data.forEach(tutor => {
        tutor.lesson_packs.forEach(pack => {
          convertedTutors.push({
            id: tutor.id,
            name: tutor.name,
            email: tutor.email,
            subject: pack.name.split(' - ')[0], // Extract subject from pack name
            lessonPackId: pack.id,
            lessonPackName: pack.name
          });
        });
      });
      
      setTutors(convertedTutors);
    } catch (err) {
      console.error('Error fetching tutors:', err);
      setError('Failed to load tutors. Please try again.');
      setTutors([]);
    } finally {
      setLoadingTutors(false);
    }
  };

  useEffect(() => {
    // When tutor is selected, fetch available time windows from backend
    if (selectedTutor) {
      fetchAvailableWindows(selectedTutor.id);
    }
  }, [selectedTutor]);

  const fetchAvailableWindows = async (tutorId) => {
    try {
      setLoadingWindows(true);
      setError(null);
      const data = await studentApi.getTutorAvailableWindows(tutorId);
      const converted = convertBackendWindows(data);
      setAvailableWindows(converted);
      setSelectedWindow(null);
      setSelectedSlot(null);
      setCustomStartTime('');
      setCustomEndTime('');
      setUseCustomTime(false);
    } catch (err) {
      console.error('Error fetching available windows:', err);
      setError('Failed to load available time slots. Please try again.');
      setAvailableWindows([]);
    } finally {
      setLoadingWindows(false);
    }
  };

  // Generate time options for custom time selection
  const generateTimeOptions = (startTime, endTime) => {
    const options = [];
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    // Generate options every 30 minutes
    for (let minutes = startMinutes; minutes <= endMinutes; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const min = minutes % 60;
      const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      options.push(timeStr);
    }
    
    return options;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDisplayInTimezone = (display) => {
    if (!display || !display.date) return null;
    return `${formatDate(display.date)}, ${formatTime(display.start_time)} - ${formatTime(display.end_time)} (${display.timezone?.replace(/_/g, ' ') || 'UTC'})`;
  };

  const handleStartBooking = () => {
    setSelectedTutor(null);
    setSelectedSlot(null);
    setShowBookingModal(true);
  };

  const handleTutorSelect = (tutor) => {
    setSelectedTutor(tutor);
    setSelectedWindow(null);
    setSelectedSlot(null);
    setCustomStartTime('');
    setCustomEndTime('');
    setUseCustomTime(false);
  };

  const handleWindowSelect = (window) => {
    setSelectedWindow(window);
    setSelectedSlot(null);
    setCustomStartTime('');
    setCustomEndTime('');
    setUseCustomTime(false);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setCustomStartTime('');
    setCustomEndTime('');
    setUseCustomTime(false);
  };

  const handleCustomTimeSelect = () => {
    setUseCustomTime(true);
    setSelectedSlot(null);
  };

  const validateCustomTime = () => {
    if (!customStartTime || !customEndTime || !selectedWindow) return false;
    
    const [startHour, startMin] = customStartTime.split(':').map(Number);
    const [endHour, endMin] = customEndTime.split(':').map(Number);
    const [windowStartHour, windowStartMin] = selectedWindow.startTime.split(':').map(Number);
    const [windowEndHour, windowEndMin] = selectedWindow.endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const windowStartMinutes = windowStartHour * 60 + windowStartMin;
    const windowEndMinutes = windowEndHour * 60 + windowEndMin;
    
    // Check if custom time is within window and valid
    return startMinutes >= windowStartMinutes &&
           endMinutes <= windowEndMinutes &&
           startMinutes < endMinutes &&
           (endMinutes - startMinutes) >= 30; // Minimum 30 minutes
  };

  const handleConfirmBooking = () => {
    if (selectedTutor && (selectedSlot || (useCustomTime && validateCustomTime()))) {
      setShowConfirmModal(true);
    }
  };

  const handleFinalConfirm = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let bookingData;
      if (useCustomTime && selectedWindow) {
        bookingData = {
          date: selectedWindow.date,
          startTime: customStartTime,
          endTime: customEndTime
        };
      } else if (selectedSlot && selectedWindow) {
        bookingData = {
          date: selectedWindow.date,
          startTime: selectedSlot.startTime,
          endTime: selectedSlot.endTime
        };
      } else {
        setLoading(false);
        return;
      }
      
      // Prepare booking payload
      const payload = {
        tutor_id: selectedTutor.id,
        lesson_pack_id: selectedTutor.lessonPackId,
        date: bookingData.date,
        start_time: bookingData.startTime,
        end_time: bookingData.endTime,
        student_timezone: studentTimezone
      };
      
      // Create booking via API
      const newBooking = await studentApi.createBooking(payload);
      
      // Convert and add to bookings list
      const converted = convertBackendBookings([newBooking])[0];
      setBookings([converted, ...bookings]);
      
      setShowConfirmModal(false);
      setShowBookingModal(false);
      setSelectedTutor(null);
      setSelectedWindow(null);
      setSelectedSlot(null);
      setCustomStartTime('');
      setCustomEndTime('');
      setUseCustomTime(false);
      setError(null);
    } catch (err) {
      console.error('Error creating booking:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create booking';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getBookingDuration = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const durationMinutes = endMinutes - startMinutes;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    if (hours === 0) {
      return `${minutes} minutes`;
    } else if (minutes === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };

  const handleCancelClick = (booking) => {
    setBookingToCancel(booking);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!bookingToCancel) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await studentApi.cancelBooking(bookingToCancel.id);
      
      // Update local state
      setBookings(bookings.map(b => 
        b.id === bookingToCancel.id 
          ? { ...b, status: 'cancelled' }
          : b
      ));
      setShowCancelModal(false);
      setBookingToCancel(null);
      setError(null);
    } catch (err) {
      console.error('Error cancelling booking:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to cancel booking';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinMeeting = (booking) => {
    // Daily.co meetings - open in new tab
    if (booking.zoomLink) {
      window.open(booking.zoomLink, '_blank', 'noopener,noreferrer');
    }
  };


  const upcomingBookings = bookings.filter(b => b.status === 'upcoming');
  const pastBookings = bookings.filter(b => b.status === 'completed' || b.status === 'cancelled');

  const getStatusBadge = (status) => {
    switch (status) {
      case 'upcoming':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <ClockIcon className="h-3 w-3 mr-1" />
            Upcoming
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="h-3 w-3 mr-1" />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Bookings</h1>
          <p className="text-gray-600 mt-1">Schedule and manage your tutoring sessions</p>
        </div>
        <button
          onClick={handleStartBooking}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <CalendarDaysIcon className="h-5 w-5 mr-2" />
          Book a Session
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loadingBookings && (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading bookings...</p>
        </div>
      )}

      {/* Upcoming Bookings */}
      {!loadingBookings && upcomingBookings.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Upcoming Sessions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingBookings.map((booking) => (
              <div key={booking.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{booking.tutorName}</h3>
                    <p className="text-sm text-gray-600">{booking.subject}</p>
                  </div>
                  {getStatusBadge(booking.status)}
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-start text-sm">
                    <CalendarDaysIcon className="h-4 w-4 mr-2 text-gray-400 shrink-0 mt-0.5" />
                    <div className="flex flex-col gap-1">
                      {booking.student_display && (
                        <>
                          <span className="font-semibold text-gray-900 text-base">
                            {formatDisplayInTimezone(booking.student_display)}
                          </span>
                          {booking.tutor_display && (
                            <span className="text-xs text-gray-500">
                              Tutor&apos;s time: {formatDisplayInTimezone(booking.tutor_display)}
                            </span>
                          )}
                        </>
                      )}
                      {!booking.student_display && !booking.tutor_display && (
                        <span className="text-gray-600">{formatDate(booking.date)}, {formatTime(booking.startTime)} - {formatTime(booking.endTime)} ({getBookingDuration(booking.startTime, booking.endTime)})</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                    {formatTime(booking.startTime)} - {formatTime(booking.endTime)} ({getBookingDuration(booking.startTime, booking.endTime)})
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <VideoCameraIcon className="h-4 w-4 mr-2 text-gray-400" />
                    {booking.zoomLink ? (
                      <button
                        onClick={() => handleJoinMeeting(booking)}
                        className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                      >
                        Join Meeting
                      </button>
                    ) : (
                      <span className="text-gray-400">Meeting link not available</span>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleCancelClick(booking)}
                    className="w-full px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Cancel Booking
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Past Bookings */}
      {!loadingBookings && pastBookings.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Past Sessions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pastBookings.map((booking) => (
              <div key={booking.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200 opacity-75">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{booking.tutorName}</h3>
                    <p className="text-sm text-gray-600">{booking.subject}</p>
                  </div>
                  {getStatusBadge(booking.status)}
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-start text-sm">
                    <CalendarDaysIcon className="h-4 w-4 mr-2 text-gray-400 shrink-0 mt-0.5" />
                    <div className="flex flex-col gap-1">
                      {booking.student_display && (
                        <>
                          <span className="font-semibold text-gray-900 text-base">
                            {formatDisplayInTimezone(booking.student_display)}
                          </span>
                          {booking.tutor_display && (
                            <span className="text-xs text-gray-500">
                              Tutor&apos;s time: {formatDisplayInTimezone(booking.tutor_display)}
                            </span>
                          )}
                        </>
                      )}
                      {!booking.student_display && !booking.tutor_display && (
                        <span className="text-gray-600">{formatDate(booking.date)}, {formatTime(booking.startTime)} - {formatTime(booking.endTime)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                    {formatTime(booking.startTime)} - {formatTime(booking.endTime)} ({getBookingDuration(booking.startTime, booking.endTime)})
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loadingBookings && bookings.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings yet</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by booking your first session with a tutor.</p>
          <div className="mt-6">
            <button
              onClick={handleStartBooking}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Book a Session
            </button>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onClose={() => setShowBookingModal(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Book a Session</h2>
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Step 1: Tutor Selection */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Step 1: Select Your Tutor</h3>
                {loadingTutors ? (
                  <div className="text-center py-8 text-gray-500">Loading tutors...</div>
                ) : tutors.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No tutors available. You need to have an active lesson pack with an assigned tutor.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {tutors.map((tutor) => (
                    <button
                      key={tutor.id}
                      onClick={() => handleTutorSelect(tutor)}
                      className={`p-4 rounded-lg border-2 transition-colors text-left ${
                        selectedTutor?.id === tutor.id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">{tutor.name}</div>
                      <div className="text-sm text-gray-600 mt-1">{tutor.subject}</div>
                      <div className="text-xs text-gray-500 mt-1">{tutor.lessonPackName}</div>
                    </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Step 2: Time Window Selection */}
              {selectedTutor && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Step 2: Choose Date & Available Time Window
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Times shown in: <span className="font-medium">{studentTimezone}</span>
                    <br />
                    <span className="text-xs text-gray-500">
                      Select an available time window, then choose a specific time or use custom timing within that window.
                    </span>
                  </p>
                  
                  {loadingWindows ? (
                    <div className="text-center py-8 text-gray-500">Loading available time slots...</div>
                  ) : availableWindows.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No available time windows for this tutor at the moment.
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 mb-4">
                      <div className="space-y-3">
                        {availableWindows.map((window) => (
                          <button
                            key={window.id}
                            onClick={() => handleWindowSelect(window)}
                            className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
                              selectedWindow?.id === window.id
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="font-medium text-gray-900 mb-1">
                              {formatDate(window.date)}
                            </div>
                            <div className="text-sm text-gray-600">
                              Available: <span className="font-medium">{formatTime(window.startTime)} - {formatTime(window.endTime)}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Select Time Within Window */}
                  {selectedWindow && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">
                        Step 3: Choose Your Preferred Time
                      </h4>
                      
                      {/* Quick Slot Options */}
                      <div className="mb-4">
                        {(() => {
                          const quickSlots = generateQuickSlots(selectedWindow).quickSlots;
                          const slotDuration = quickSlots.length > 0 ? quickSlots[0].duration : 60;
                          return (
                            <>
                              <p className="text-sm text-gray-600 mb-3">
                                Quick Options {quickSlots.length > 0 && `(${slotDuration}-minute sessions)`}:
                              </p>
                              {quickSlots.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                  {quickSlots.map((slot, index) => (
                                    <button
                                      key={index}
                                      onClick={() => handleSlotSelect(slot)}
                                      className={`p-2 rounded-lg border-2 transition-colors text-sm ${
                                        selectedSlot?.startTime === slot.startTime && !useCustomTime
                                          ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                      }`}
                                    >
                                      <div>{formatTime(slot.startTime)}</div>
                                      <div className="text-xs">- {formatTime(slot.endTime)}</div>
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 italic">
                                  No quick slots available for this window. Please use custom time selection below.
                                </p>
                              )}
                            </>
                          );
                        })()}
                      </div>

                      {/* Custom Time Selection */}
                      <div className="border-t border-gray-300 pt-4">
                        <button
                          onClick={handleCustomTimeSelect}
                          className={`w-full p-3 mb-4 rounded-lg border-2 transition-colors text-left ${
                            useCustomTime
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium text-gray-900">
                            ✏️ Choose Custom Time Range
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Select your own start and end time within the available window
                          </div>
                        </button>

                        {useCustomTime && (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Start Time
                              </label>
                              <select
                                value={customStartTime}
                                onChange={(e) => {
                                  setCustomStartTime(e.target.value);
                                  // Auto-adjust end time if it's before start time
                                  if (customEndTime && e.target.value >= customEndTime) {
                                    setCustomEndTime('');
                                  }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              >
                                <option value="">Select start time</option>
                                {generateTimeOptions(selectedWindow.startTime, selectedWindow.endTime).map((time) => (
                                  <option key={time} value={time}>
                                    {formatTime(time)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                End Time
                              </label>
                              <select
                                value={customEndTime}
                                onChange={(e) => setCustomEndTime(e.target.value)}
                                disabled={!customStartTime}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              >
                                <option value="">Select end time</option>
                                {customStartTime && generateTimeOptions(customStartTime, selectedWindow.endTime)
                                  .filter(time => time > customStartTime)
                                  .map((time) => (
                                    <option key={time} value={time}>
                                      {formatTime(time)}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>
                        )}

                        {useCustomTime && customStartTime && customEndTime && (
                          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                            <div className="text-sm font-medium text-blue-900">
                              Selected: {formatTime(customStartTime)} - {formatTime(customEndTime)}
                            </div>
                            <div className="text-xs text-blue-700 mt-1">
                              Duration: {getBookingDuration(customStartTime, customEndTime)}
                            </div>
                            {!validateCustomTime() && (
                              <div className="text-xs text-red-600 mt-2">
                                ⚠️ Please ensure end time is after start time and duration is at least 30 minutes
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmBooking}
                  disabled={!selectedTutor || !(selectedSlot || (useCustomTime && validateCustomTime()))}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onClose={() => setShowConfirmModal(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Confirm Booking</h2>
              
              {selectedTutor && selectedWindow && (selectedSlot || (useCustomTime && customStartTime && customEndTime)) && (
                <div className="space-y-3 mb-6">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Tutor:</span>
                    <p className="text-gray-900">{selectedTutor.name}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Subject:</span>
                    <p className="text-gray-900">{selectedTutor.subject}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Date:</span>
                    <p className="text-gray-900">{formatDate(selectedWindow.date)}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Time:</span>
                    <p className="text-gray-900">
                      {useCustomTime 
                        ? `${formatTime(customStartTime)} - ${formatTime(customEndTime)}`
                        : `${formatTime(selectedSlot.startTime)} - ${formatTime(selectedSlot.endTime)}`
                      }
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Duration:</span>
                    <p className="text-gray-900">
                      {useCustomTime
                        ? getBookingDuration(customStartTime, customEndTime)
                        : getBookingDuration(selectedSlot.startTime, selectedSlot.endTime)
                      }
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Timezone:</span>
                    <p className="text-gray-900">{studentTimezone}</p>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFinalConfirm}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {loading ? 'Booking...' : 'Confirm Booking'}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Cancel Confirmation Modal */}
      <Dialog open={showCancelModal} onClose={() => setShowCancelModal(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Cancel Booking</h2>
              
              {bookingToCancel && (
                <>
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to cancel your session with <strong>{bookingToCancel.tutorName}</strong> on{' '}
                    <strong>{formatDate(bookingToCancel.date)}</strong> at{' '}
                    <strong>{formatTime(bookingToCancel.startTime)}</strong>?
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    This action cannot be undone.
                  </p>
                </>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Keep Booking
                </button>
                <button
                  onClick={handleCancelConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                >
                  Cancel Booking
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

          {/* Video Meeting Component - Not needed for Daily.co (opens directly) */}
    </div>
  );
};

export default Bookings;

